import cx from 'classnames';
import { canUseDOM } from 'exenv';
import { t } from 'i18next';
import _ from 'lodash';
import { MouseEvent, FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Col } from '../../../../../../../shared/frontend/components/grid/Col';
import { Row } from '../../../../../../../shared/frontend/components/grid/Row';
import { Media } from '../../../../../../../shared/frontend/components/presentational/media';
import { CoveragePushUpsell } from '../../../../../../../shared/frontend/components/push/CoveragePushUpsell';
import { LazyLoadableCoverageBlurbs as LoadableCoverageBlurbs } from '../../../../../../../shared/frontend/scripts/thread/components/lazyCoverageBlurbs';
import { asset } from '../../../../../../../shared/frontend/services/assetResolverService';
import BossKillsStrip from '../../../globalCoverage/components/bossKillsStrip';
import {
  applyPartnerOverrides,
  chooseBestPartner,
  getActivePartner,
  TRWFPartner
} from '../../../globalCoverage/components/partnerTheme';
import RaceVisualizer from '../../../globalCoverage/components/raceVisualizer';
import { Link, TrackedLink } from '../../../../../../../shared/frontend/components/link';
import { InteractiveStreamPreview, MEDIUM_SIZE } from './shared';
import { GuildShowdown } from './guildShowdown';
import { safeParseInt, safeParseJson } from '../../../../../../../shared/common/utils';
import { useFeatures, useSiteConfig } from '../../../../../../../shared/frontend/scripts/selectors/global';

type TTierMn1WorldFirstProps = {
  streamers: any[];
  className?: string;
};

const RAID_SLUG = 'tier-mn-1';
const RAID_SNAKE_CASE = RAID_SLUG.replace(/-/g, '_');
const RAID_EXPANSION = 'midnight';

const PARTNERS: TRWFPartner[] = [
  {
    isStreamActive: true,
    weight: 20,
    streamSchedule: [[500, 2000]],
    title: '',
    name: 'Echo Esports',
    slug: 'echo',
    guildLogo: `/images/rwf/${RAID_SLUG}/echo/logo.png`,
    embedImage: `/images/rwf/${RAID_SLUG}/echo/logo.png`,
    backdropImage: `/images/rwf/${RAID_SLUG}/echo/backdrop.png`,
    logoWidth: 140,
    logoHeight: 140,
    logoLink: 'https://www.twitch.tv/echo_esports',
    twitch: 'echo_esports',
    twitter: 'EchoGuild',
    facebook: '',
    instagram: 'echoguild',
    youtube: 'echoesports',
    discord: 'T5vTbydUSw',
    web: '', // TODO: update with tier-mn-1 partner link
    streamType: 'twitch',
    isPartner: true
  },
  {
    isStreamActive: true,
    weight: 15,
    streamSchedule: [[1700, 800]],
    title: '',
    name: 'Liquid',
    slug: 'liquid',
    guildLogo: `/images/rwf/${RAID_SLUG}/liquid/logo.png`,
    embedImage: `/images/rwf/${RAID_SLUG}/liquid/logo.png`,
    backdropImage: `/images/rwf/${RAID_SLUG}/liquid/backdrop.png`,
    logoWidth: 140,
    logoHeight: 140,
    logoLink: 'https://www.twitch.tv/teamliquid',
    twitch: 'teamliquid',
    twitter: 'LiquidGuild',
    instagram: 'teamliquid',
    youtube: '@TeamLiquidMMO',
    discord: 'liquidguild',
    web: '', // TODO: update with tier-mn-1 partner link
    streamType: 'twitch',
    isPartner: true
  },
  {
    isStreamActive: true,
    weight: 10,
    streamSchedule: [
      [500, 2300],
      [800, 2300]
    ],
    title: '',
    name: 'Method',
    slug: 'method',
    guildLogo: `/images/rwf/${RAID_SLUG}/method/logo.png`,
    embedImage: `/images/rwf/${RAID_SLUG}/method/logo.png`,
    backdropImage: `/images/rwf/${RAID_SLUG}/method/backdrop.png`,
    logoWidth: 140,
    logoHeight: 140,
    logoLink: 'https://www.twitch.tv/Method',
    twitch: 'Method',
    twitter: 'Method',
    facebook: 'methodgg',
    instagram: 'method_gg',
    youtube: 'c/Methodgg',
    discord: 'method',
    web: '', // TODO: update with tier-mn-1 partner link
    streamType: 'twitch',
    isPartner: true
  }
];

export function isTierMn1RWFActive(props: { currentFeatures: { [key: string]: boolean } }) {
  return !!props.currentFeatures[`rwf_${RAID_SNAKE_CASE}_masthead`];
}

const TierMn1WorldFirstBase: FC<TTierMn1WorldFirstProps> = memo(({ streamers, className }) => {
  const siteConfig = useSiteConfig();
  const currentFeatures = useFeatures();

  const [activePartner, setActivePartner] = useState<string>('');

  const partnersWithOverrides = useMemo(() => {
    const partners = [...PARTNERS];
    applyPartnerOverrides(partners, currentFeatures.params[`rwf_${RAID_SNAKE_CASE}_partner_overrides`]);
    return partners;
  }, [currentFeatures.params]);

  useEffect(() => {
    if (partnersWithOverrides.length === 0) {
      return;
    }

    const randomPartner = partnersWithOverrides[siteConfig.seed2 % partnersWithOverrides.length];

    if (!canUseDOM) {
      setActivePartner(randomPartner.slug);
      return;
    }

    const locationSearch = window.location.search;
    const partner = getActivePartner(partnersWithOverrides, locationSearch);

    if (partner) {
      setActivePartner(partner.slug);
    } else {
      const bestPartner = chooseBestPartner(partnersWithOverrides, siteConfig.militaryTime, siteConfig.seed / 0xffffff);
      if (bestPartner) {
        setActivePartner(bestPartner.slug);
      } else {
        setActivePartner(randomPartner.slug);
      }
    }
  }, [partnersWithOverrides, siteConfig.militaryTime]);

  const choosePartner = useCallback((partnerSlug: string, ev: MouseEvent<HTMLAnchorElement>) => {
    ev.preventDefault();
    setActivePartner(partnerSlug);

    if (canUseDOM) {
      const elem = document.getElementById('application');
      if (elem) {
        elem.style.background = '';
      }
    }
  }, []);

  const primaryStream = useMemo(
    () => ({
      live: true,
      autoplay: true,
      ...streamers[0],
      size: MEDIUM_SIZE,
      className: 'slds-col slds-grow-none'
    }),
    [streamers]
  );

  const partner = useMemo(() => {
    if (activePartner) {
      return partnersWithOverrides.find((p) => p.slug === activePartner);
    }

    return partnersWithOverrides[siteConfig.seed2 % partnersWithOverrides.length] || null;
  }, [partnersWithOverrides, activePartner, siteConfig.seed2]);

  const socials = useMemo(() => {
    if (!partner) {
      return [];
    }

    const socialLinks = [];

    if (partner.twitch) {
      socialLinks.push(
        <TrackedLink
          className="rio-spaced-element--large"
          target="_blank"
          key="twitch"
          href={`https://twitch.tv/${partner.twitch}`}
        >
          <span className="slds-avatar slds-avatar--small rio-social twitch" />
        </TrackedLink>
      );
    }

    if (partner.twitter) {
      socialLinks.push(
        <TrackedLink
          className="rio-spaced-element--large"
          target="_blank"
          key="twitter"
          href={`https://twitter.com/${partner.twitter}`}
        >
          <span className="slds-avatar slds-avatar--small rio-social twitter" />
        </TrackedLink>
      );
    }

    if (partner.facebook) {
      socialLinks.push(
        <TrackedLink
          className="rio-spaced-element--large"
          target="_blank"
          key="facebook"
          href={`https://www.facebook.com/${partner.facebook}`}
        >
          <span className="slds-avatar slds-avatar--small rio-social facebook" />
        </TrackedLink>
      );
    }

    if (partner.youtube) {
      socialLinks.push(
        <TrackedLink
          className="rio-spaced-element--large"
          target="_blank"
          key="youtube"
          href={`https://youtube.com/${partner.youtube}`}
        >
          <span className="slds-avatar slds-avatar--small rio-social youtube" />
        </TrackedLink>
      );
    }

    if (partner.instagram) {
      socialLinks.push(
        <TrackedLink
          className="rio-spaced-element--large"
          target="_blank"
          key="instagram"
          href={`https://instagram.com/${partner.instagram}`}
        >
          <span className="slds-avatar slds-avatar--small rio-social instagram" />
        </TrackedLink>
      );
    }

    if (partner.discord) {
      socialLinks.push(
        <TrackedLink
          className="rio-spaced-element--large"
          target="_blank"
          key="discord"
          href={`https://discord.gg/${partner.discord}`}
        >
          <span className="slds-avatar slds-avatar--small rio-social discord" />
        </TrackedLink>
      );
    }

    if (partner.web) {
      socialLinks.push(
        <TrackedLink className="rio-spaced-element--large" target="_blank" key="web" href={`${partner.web}`}>
          <span className="slds-avatar slds-avatar--small rio-social web" />
        </TrackedLink>
      );
    }

    return socialLinks;
  }, [partner]);

  const isShowdownActive = (currentFeatures as any)[`rwf_${RAID_SNAKE_CASE}_showdown`];

  const getStreamComponent = useCallback(
    (overrideLive?: boolean) => {
      if (!partner) {
        return null;
      }

      const sampledStreamId = partner.streamIds?.[siteConfig.seed % (partner.streamIds?.length || 1)];

      return (
        <InteractiveStreamPreview
          key={primaryStream.stream.name}
          type={partner.streamType}
          thumbnailUrl={primaryStream.stream.thumbnail_url}
          width={primaryStream.size.width}
          height={primaryStream.size.height}
          thumbnailWidth={primaryStream.size.width}
          thumbnailHeight={primaryStream.size.height}
          streamIdentifier={partner.twitch! || partner.douyu! || sampledStreamId || ''}
          isFeatured={true}
          featuredTitle={partner.title}
          makeLiveImmediately={true}
          live={overrideLive ?? true}
          autoplay={true}
          className={cx('stream--larger', primaryStream.className)}
        />
      );
    },
    [partner, primaryStream]
  );

  const currentPathname = canUseDOM ? window.location.pathname : '';

  if (!partner) {
    return null;
  }

  return (
    <>
      <div
        key="streams"
        className={cx('rio-fuzzy-border rio-fuzzy-border--large', className)}
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.55)), url(${asset(
            `/images/${RAID_SLUG}/headers/_zone.jpg`
          )})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center top'
        }}
      >
        <h3 className="slds-text-align--center slds-p-horizontal--large slds-p-top--medium slds-m-bottom--x-small">
          <p className="slds-text-heading--large text-bold rio-text--shadow">
            {t('mastheads:rwf.shared.title', { raidName: t(`raids:${RAID_SLUG}.name`) })}
          </p>
          <p className="slds-text-heading--small slds-m-top--x-small rio-text--shadow">
            {t('mastheads:rwf.shared.title-subtext', { expansion: t(`common:expansions.${RAID_EXPANSION}.name`) })}
          </p>
        </h3>

        <Row
          className={'slds-text-align--center slds-p-bottom--medium  slds-p-horizontal--large slds-p-top--small'}
          wrap={{
            md: false,
            xs: true
          }}
          verticalAlign="center"
          align="center"
          gutter="x-small"
        >
          <Col className="rio-takeover-hidden rio-hidden--small rio-hidden--medium slds-shrink-none" grow={false}>
            <TrackedLink href={partner.logoLink} as={`rwf:${partner.slug}`} target="_blank">
              <img
                src={asset(partner.embedImage! || partner.guildLogo!, { width: 280 })}
                style={{
                  minWidth: partner.logoWidth,
                  width: partner.logoWidth
                }}
              />
            </TrackedLink>
          </Col>

          <Col>
            <Media at="sm">{getStreamComponent()}</Media>
            <Media greaterThan="sm">{getStreamComponent()}</Media>
            {partner.customLink && (
              <div className="slds-m-bottom--x-small">
                <TrackedLink href={partner.customLink.url}>
                  {partner.customLink.title} &nbsp; <i className="fa fa-external-link"></i>
                </TrackedLink>
              </div>
            )}
            <div>{socials}</div>
          </Col>

          <Col className="rio-hidden--small slds-text-align--center">
            <img src={asset('/images/redbull/rwf_divider.png')} style={{ height: '100%', maxWidth: 1 }} />
          </Col>

          <Col>
            <CoveragePushUpsell buttonOnly className="slds-m-bottom--xxx-small" />

            <LoadableCoverageBlurbs
              skeletonOnLoading
              base_url={`/${RAID_SLUG}/global-coverage`}
              slug={`${RAID_SLUG}-global-coverage`}
              className="slds-m-top--x-small"
            />

            <div className="slds-text-align--right slds-m-top--small">
              <Link to={`/${RAID_SLUG}/global-coverage`}>{t('mastheads:rwf.shared.more-highlights')} &raquo;</Link>
            </div>
          </Col>

          <Col className="rio-hidden--small slds-text-align--center">
            <img src={asset('/images/redbull/rwf_divider.png')} style={{ height: '100%', maxWidth: 1 }} />
          </Col>

          <Col>
            <div className="slds-m-bottom--small text-raiderio slds-text-heading--small text-bold">
              {t('mastheads:rwf.shared.partners')}
            </div>
            {partnersWithOverrides
              .filter((p) => p.isPartner)
              .map((otherPartner: any) => (
                <div key={otherPartner.slug}>
                  <Link
                    className={cx(
                      'nowrap rio-realm-link',
                      otherPartner.slug === partner.slug ? 'text-bold text-raiderio' : ''
                    )}
                    to={`${currentPathname}#${otherPartner.slug}`}
                    onClick={(ev) => choosePartner(otherPartner.slug, ev)}
                  >
                    {otherPartner.name}
                  </Link>
                </div>
              ))}
            <div
              style={{
                borderTop: '1.5px solid rgba(255, 255, 255, 0.125)',
                width: '65%',
                margin: '10px auto'
              }}
            ></div>

            {partnersWithOverrides
              .filter((p) => !p.isPartner)
              .map((otherPartner: any) => (
                <div key={otherPartner.slug}>
                  <Link
                    className={cx(
                      'nowrap rio-realm-link',
                      otherPartner.slug === partner.slug ? 'text-bold text-raiderio' : ''
                    )}
                    to={`${currentPathname}#${otherPartner.slug}`}
                    onClick={(ev) => choosePartner(otherPartner.slug, ev)}
                  >
                    {otherPartner.name}
                  </Link>
                </div>
              ))}
          </Col>
        </Row>
        {isShowdownActive && (
          <GuildShowdown
            raidSlug={RAID_SLUG}
            showdownTitle={currentFeatures.params.rwf_showdown_boss_name}
            bossSlug={currentFeatures.params.rwf_showdown_boss_slug}
            numVisibleStreams={safeParseInt(currentFeatures.params.rwf_showdown_num_visible_streams, 2)}
            guilds={safeParseJson(currentFeatures.params.rwf_showdown_guilds) || []}
            graphGuildQueries={currentFeatures.params[`rwf_${RAID_SNAKE_CASE}_showdown_graph_guilds`] || ''}
            showVersus={(currentFeatures as any)[`rwf_${RAID_SNAKE_CASE}_showdown_versus`]}
            showGraph={(currentFeatures as any)[`rwf_${RAID_SNAKE_CASE}_showdown_graph`]}
            showTimeline={(currentFeatures as any)[`rwf_${RAID_SNAKE_CASE}_showdown_timeline`]}
            timelineWidgetQuery={currentFeatures.params[`rwf_${RAID_SNAKE_CASE}_showdown_timeline_widget_query`]}
          />
        )}
      </div>

      {(currentFeatures as any)[`rwf_${RAID_SNAKE_CASE}_race_visualizer`] && (
        <>
          <BossKillsStrip raidSlug={RAID_SLUG} regionSlug="world" difficulty="mythic" className="slds-m-top--large" />

          <RaceVisualizer raid={RAID_SLUG} region="world" style={{ maxWidth: '100%' }} />
        </>
      )}
    </>
  );
});

export const TierMn1WorldFirst = TierMn1WorldFirstBase;
